import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Grid } from '@visx/grid';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import {
  useTooltip,
  useTooltipInPortal,
  defaultStyles as tooltipDefaultStyles,
} from '@visx/tooltip';
import { localPoint } from '@visx/event';

import { DateAxisBottom } from 'js/components/Charts/Elements/DateAxisBottom';
import { YearUnderlay } from 'js/components/Charts/Elements/YearUnderlay';
import { useClosestLineHover } from 'js/hooks/useClosestLineHover';
import { timeFormatWeek } from 'js/utils/time-format';

import styles from './BriefingItemChart.module.scss';
import sharedStyles from './OrgAnalyze.module.scss';

import LegendSwatch from './LegendSwatch';
import {
  DEFAULT_MARGINS,
  TEAM_HOVER_MAX_DISTANCE,
  buildLines,
  clusterLinesAtX,
  formatDelta,
  labelForCycle,
  lineId,
  lineVisual,
  previousPoint,
} from './briefing-chart-lines';

// Only the box gets translucent -- text/labels stay at full opacity, since
// backgroundColor's alpha channel doesn't touch descendant content.
const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.88)',
  padding: '0.4rem 0.6rem',
  fontSize: '0.75rem',
  // Rendered in a portal on <body>, so it has to clear the app's sidebar.
  zIndex: 2000,
};

// Measure question text can run to a full sentence; the tooltip shows a
// bounded slice of it (the full text is on the label's title).
const TOOLTIP_LABEL_MAX_CHARS = 60;

function truncateLabel(label) {
  if (typeof label !== 'string' || label.length <= TOOLTIP_LABEL_MAX_CHARS) {
    return label;
  }
  return `${label.slice(0, TOOLTIP_LABEL_MAX_CHARS - 1).trimEnd()}…`;
}

export default function BriefingItemChartPlot({
  chart,
  paletteById = {},
  width = 0,
  height = 0,
  margins = DEFAULT_MARGINS,
  hoveredLineId = null,
  onHoveredLineId,
}) {
  const { lines, useTimeAxis } = useMemo(
    () => buildLines(chart?.lines ?? []),
    [chart]
  );
  margins = { ...DEFAULT_MARGINS, ...margins };
  const chartWidth = Math.max(width - margins.left - margins.right, 0);
  const chartHeight = Math.max(height - margins.top - margins.bottom, 0);

  const cycleCount = lines.reduce(
    (max, line) => Math.max(max, line.points.length),
    0
  );
  const isTeamChart = chart?.kind === 'teams';

  const xScale = useMemo(() => {
    if (useTimeAxis) {
      const times = lines.flatMap((line) =>
        line.points.map((point) => point.date.getTime())
      );
      if (times.length === 0) {
        return null;
      }
      return scaleTime({
        domain: [new Date(Math.min(...times)), new Date(Math.max(...times))],
        range: [0, chartWidth],
      });
    }
    return scaleLinear({
      domain: [0, Math.max(cycleCount - 1, 1)],
      range: [0, chartWidth],
    });
  }, [useTimeAxis, lines, cycleCount, chartWidth]);

  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: [0, 100],
        range: [chartHeight, 0],
      }),
    [chartHeight]
  );

  const pointX = useCallback(
    (point) =>
      useTimeAxis ? xScale?.(point.date) ?? 0 : xScale?.(point.cycle) ?? 0,
    [useTimeAxis, xScale]
  );
  const pointY = useCallback((point) => yScale(point.score), [yScale]);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  // The tooltip renders on <body> so no sibling (the sidebar) can stack over
  // it. Its left/top stay relative to the svg, which is the container.
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const proximitySeries = useMemo(
    () => lines.map((line) => ({ id: lineId(line), data: line.points, line })),
    [lines]
  );

  const chartMargin = useMemo(
    () => ({ left: margins.left, top: margins.top }),
    [margins.left, margins.top]
  );

  const { resolveMatch } = useClosestLineHover({
    series: proximitySeries,
    x: pointX,
    y: pointY,
    curve: curveMonotoneX,
    maxDistance: TEAM_HOVER_MAX_DISTANCE,
    margin: chartMargin,
  });

  const clearHover = useCallback(() => {
    hideTooltip();
    onHoveredLineId?.(null);
  }, [hideTooltip, onHoveredLineId]);

  const handlePointerMove = useCallback(
    (event) => {
      if (lines.length === 0 || chartWidth <= 0 || !xScale) {
        return;
      }
      const point = localPoint(event);
      if (!point) {
        return;
      }
      const pointerX = point.x - margins.left;
      const { targetX, entries } = clusterLinesAtX(lines, pointerX, pointX);

      const hoveredId = isTeamChart ? resolveMatch(event)?.id ?? null : null;
      onHoveredLineId?.(hoveredId ? String(hoveredId) : null);

      const values = entries
        .map(({ line, point: clusterPoint }) => {
          const index = lines.indexOf(line);
          const prior = previousPoint(line, clusterPoint);
          const score = clusterPoint.score;
          const previousScore = prior?.score;
          const delta =
            typeof score === 'number' && typeof previousScore === 'number'
              ? score - previousScore
              : null;

          return {
            id: lineId(line),
            label: line.label,
            score,
            delta,
            point: clusterPoint,
            when:
              useTimeAxis && clusterPoint.date
                ? timeFormatWeek(clusterPoint.date)
                : null,
            visual: lineVisual(line, chart.kind, index, paletteById),
          };
        })
        .sort((a, b) => b.score - a.score);

      if (values.length === 0) {
        hideTooltip();
        return;
      }

      showTooltip({
        tooltipLeft: margins.left + targetX,
        tooltipTop: margins.top,
        tooltipData: {
          values,
          clusterX: targetX,
          hoveredLineId: hoveredId ? String(hoveredId) : null,
        },
      });
    },
    [
      lines,
      chartWidth,
      xScale,
      margins.left,
      margins.top,
      pointX,
      chart?.kind,
      isTeamChart,
      paletteById,
      resolveMatch,
      onHoveredLineId,
      showTooltip,
      hideTooltip,
      useTimeAxis,
    ]
  );

  if (
    !chart ||
    lines.length === 0 ||
    cycleCount === 0 ||
    chartWidth <= 0 ||
    !xScale
  ) {
    return null;
  }

  const tooltipValues = tooltipData?.values ?? [];
  const mixedTooltipDates =
    new Set(tooltipValues.map((entry) => entry.when).filter(Boolean)).size > 1;
  const lineEntries = lines.map((line, index) => ({
    line,
    index,
    id: lineId(line),
    visual: lineVisual(line, chart.kind, index, paletteById),
  }));
  const highlightedId = isTeamChart ? hoveredLineId : null;
  const backgroundEntries = highlightedId
    ? lineEntries.filter((entry) => entry.id !== highlightedId)
    : lineEntries;
  const highlightedEntry = highlightedId
    ? lineEntries.find((entry) => entry.id === highlightedId)
    : null;

  const renderLine = (entry) => {
    const { line, id, visual } = entry;
    const isHovered = Boolean(highlightedId && highlightedId === id);
    const isHoveredTeam = isHovered && visual.neutral;

    return (
      <g
        key={id}
        className={cx(styles.itemChartLine, visual.paletteClassName, {
          [styles.itemChartTeamHighlight]: isHoveredTeam,
          [styles.itemChartLineHover]: isHovered,
        })}
      >
        <LinePath
          data={line.points}
          x={pointX}
          y={pointY}
          curve={curveMonotoneX}
          className={cx(
            isHoveredTeam
              ? 'chart-highlight-line'
              : visual.neutral
              ? 'chart-neutral-line'
              : 'palette-stroke',
            { [styles.itemChartOverallLine]: visual.dashed }
          )}
          fill="none"
        />
        {line.points.map((point) => {
          const inTooltip =
            tooltipOpen &&
            tooltipValues.some(
              (value) => value.id === id && value.point === point
            );

          return (
            <circle
              key={point.cycle}
              cx={pointX(point)}
              cy={pointY(point)}
              r={inTooltip ? 4 : isHovered ? 3.5 : 2.5}
              className={cx(
                isHoveredTeam
                  ? styles.itemChartTeamFocusDot
                  : visual.neutral
                  ? styles.itemChartNeutralPoint
                  : 'palette-fill',
                {
                  [styles.itemChartPointIdle]: tooltipOpen && !inTooltip,
                }
              )}
              stroke="none"
            />
          );
        })}
      </g>
    );
  };

  return (
    <>
      <svg
        ref={containerRef}
        width={width}
        height={height}
        className="chart-light"
      >
        <g transform={`translate(${margins.left}, ${margins.top})`}>
          {useTimeAxis ? (
            <YearUnderlay height={chartHeight} xScale={xScale} />
          ) : null}
          <Grid
            className="chart-grid chart-neutral-line"
            width={chartWidth}
            height={chartHeight}
            xScale={xScale}
            yScale={yScale}
            rowTickValues={[0, 50, 100]}
            numTicksColumns={Math.min(cycleCount, 6)}
            stroke="#526067"
          />
          <AxisLeft
            scale={yScale}
            tickValues={[0, 50, 100]}
            tickFormat={(d) => `${d}%`}
            stroke="#8a97a0"
            tickStroke="#8a97a0"
            tickLabelProps={() => ({
              fill: '#6c757d',
              fontSize: 9,
              textAnchor: 'end',
              dy: '0.3em',
              dx: '-0.3em',
            })}
          />
          {useTimeAxis ? (
            <DateAxisBottom
              chartHeight={chartHeight}
              chartWidth={chartWidth}
              xScale={xScale}
              className="chart-axis axis-solid"
              axisLineProps={{ strokeDasharray: 'none' }}
              tickLineProps={{ className: 'tick', strokeDasharray: 'none' }}
            />
          ) : (
            <AxisBottom
              top={chartHeight}
              scale={xScale}
              numTicks={Math.min(cycleCount, 6)}
              tickFormat={(d) => labelForCycle(d, cycleCount)}
              axisClassName="chart-axis axis-solid"
              axisLineProps={{ strokeDasharray: 'none', stroke: '#8a97a0' }}
              stroke="#8a97a0"
              tickStroke="#8a97a0"
              tickLabelProps={() => ({
                fill: '#6c757d',
                fontSize: 9,
                textAnchor: 'middle',
              })}
            />
          )}
          <g pointerEvents="none">
            {backgroundEntries.map(renderLine)}
            {highlightedEntry ? renderLine(highlightedEntry) : null}
            {tooltipOpen && tooltipData?.clusterX != null ? (
              <line
                x1={tooltipData.clusterX}
                x2={tooltipData.clusterX}
                y1={0}
                y2={chartHeight}
                className={styles.itemChartFocusRule}
              />
            ) : null}
            {tooltipOpen
              ? tooltipValues.map((entry) => (
                  <g
                    key={`focus-${entry.id}`}
                    className={entry.visual.paletteClassName}
                  >
                    <line
                      x1={tooltipData.clusterX}
                      x2={pointX(entry.point)}
                      y1={pointY(entry.point)}
                      y2={pointY(entry.point)}
                      className={cx(styles.itemChartFocusLeader, {
                        [styles.itemChartTeamFocusLeader]:
                          entry.id === hoveredLineId && entry.visual.neutral,
                      })}
                    />
                    <circle
                      cx={pointX(entry.point)}
                      cy={pointY(entry.point)}
                      r={7}
                      fill="none"
                      className={cx(
                        styles.itemChartFocusRing,
                        entry.visual.paletteClassName ? 'palette-stroke' : null,
                        {
                          [styles.itemChartTeamFocusRing]:
                            entry.id === hoveredLineId && entry.visual.neutral,
                        }
                      )}
                    />
                  </g>
                ))
              : null}
          </g>
          <rect
            width={chartWidth}
            height={chartHeight}
            fill="transparent"
            onMouseMove={handlePointerMove}
            onMouseLeave={clearHover}
          />
        </g>
      </svg>
      {tooltipOpen && tooltipData ? (
        <TooltipInPortal
          left={tooltipLeft}
          top={tooltipTop}
          style={{ ...tooltipDefaultStyles, ...TOOLTIP_STYLE }}
          className={styles.itemChartTooltip}
          data-copy-exclude=""
        >
          {tooltipValues.map((entry) => (
            <div
              key={entry.id}
              className={cx(styles.itemChartTooltipRow, {
                [styles.itemChartTooltipRowActive]:
                  entry.id === tooltipData.hoveredLineId,
              })}
            >
              <LegendSwatch
                dashed={entry.visual.dashed}
                paletteClassName={entry.visual.paletteClassName}
                dotClassName={styles.itemChartTooltipDot}
              />
              <span className={styles.itemChartTooltipIdentity}>
                <span
                  className={styles.itemChartTooltipLabel}
                  title={entry.label}
                >
                  {truncateLabel(entry.label)}
                </span>
                {mixedTooltipDates && entry.when ? (
                  <span className={styles.itemChartTooltipWhenRow}>
                    {entry.when}
                  </span>
                ) : null}
              </span>
              <span className={styles.itemChartTooltipValue}>
                {Math.round(entry.score)}%
              </span>
              <span
                className={`${styles.itemChartTooltipDelta} ${
                  entry.delta > 0
                    ? sharedStyles.deltaImprove
                    : entry.delta < 0
                    ? sharedStyles.deltaDecline
                    : ''
                }`}
              >
                {formatDelta(entry.delta) || ''}
              </span>
            </div>
          ))}
        </TooltipInPortal>
      ) : null}
    </>
  );
}
